/**
 * OutlookSignatureManager
 *
 * Outlook 2.0 — standalone signature management panel.
 *
 * Features:
 *  • List all signatures for the logged-in user
 *  • Create a new signature (name + contentEditable rich editor)
 *  • Edit an existing signature inline
 *  • Delete a signature (with confirmation)
 *  • Set / unset the default signature (stored in localStorage per user)
 *    — Default signature is auto-inserted when composing a new email
 *
 * Default storage key: `outlook_default_sig_<employeeId>`
 * Value: signature id string, or empty string for none
 */

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Tooltip, Spin, Modal } from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  CheckCircleOutlined,
  LoadingOutlined,
  CloseOutlined,
  SaveOutlined,
} from '@ant-design/icons';

import {
  fetchAllSignatures,
  saveSignature,
  updateSignatureThunk,
  deleteSignatureThunk,
} from '../../../Slices/salesSlice';

// ─────────────────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────────────────
const C = {
  blue:      '#0f6cbd',
  blueDark:  '#115ea3',
  blueLight: '#eff6fc',
  text:      '#242424',
  textSec:   '#605e5c',
  textMuted: '#8a8886',
  border:    '#edebe9',
  bg:        '#ffffff',
  bgAlt:     '#faf9f8',
  red:       '#c50f1f',
  redBg:     '#fde7e9',
  green:     '#107c10',
};
const FONT = "'Segoe UI', Tahoma, sans-serif";

// ─────────────────────────────────────────────────────────────────────────────
// localStorage helpers
// ─────────────────────────────────────────────────────────────────────────────
function getDefaultSigKey(employeeId) {
  return `outlook_default_sig_${employeeId}`;
}

export function getDefaultSignatureId(employeeId) {
  return localStorage.getItem(getDefaultSigKey(employeeId)) || '';
}

function setDefaultSignatureId(employeeId, sigId) {
  if (sigId) {
    localStorage.setItem(getDefaultSigKey(employeeId), sigId);
  } else {
    localStorage.removeItem(getDefaultSigKey(employeeId));
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Mini rich editor — contentEditable with a small toolbar
// ─────────────────────────────────────────────────────────────────────────────
function SigEditor({ value, onChange, placeholder = 'Type your signature here…' }) {
  const editorRef = useRef(null);
  const [empty, setEmpty] = useState(true);

  // Populate editor when value is set from outside (edit mode)
  useEffect(() => {
    const el = editorRef.current;
    if (!el) return;
    if (el.innerHTML !== value) {
      el.innerHTML = value || '';
      setEmpty(!value);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleInput = useCallback(() => {
    const el = editorRef.current;
    if (!el) return;
    const html = el.innerHTML;
    const isEmpty = el.innerText.trim() === '' && !el.querySelector('img, table');
    setEmpty(isEmpty);
    onChange(isEmpty ? '' : html);
  }, [onChange]);

  const exec = (cmd, val = null) => {
    editorRef.current?.focus();
    document.execCommand(cmd, false, val);
  };

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 4, overflow: 'hidden' }}>
      {/* Mini toolbar */}
      <div style={{
        display: 'flex', gap: 2, padding: '4px 8px',
        borderBottom: `1px solid ${C.border}`, background: C.bgAlt, flexWrap: 'wrap',
      }}>
        {[
          { label: <strong>B</strong>,  title: 'Bold',          cmd: 'bold' },
          { label: <em>I</em>,          title: 'Italic',        cmd: 'italic' },
          { label: <u>U</u>,            title: 'Underline',     cmd: 'underline' },
          { label: 'Link',              title: 'Insert link',   cmd: 'createLink', prompt: true },
        ].map(({ label, title, cmd, prompt: needsPrompt }) => (
          <button
            key={cmd}
            title={title}
            onMouseDown={(e) => {
              e.preventDefault();
              if (needsPrompt) {
                const url = window.prompt('URL:');
                if (url) exec(cmd, url.startsWith('http') ? url : `https://${url}`);
              } else {
                exec(cmd);
              }
            }}
            style={{
              background: 'none', border: `1px solid ${C.border}`, borderRadius: 3,
              padding: '1px 8px', cursor: 'pointer', fontSize: 12,
              fontFamily: FONT, color: C.textSec,
            }}
          >
            {label}
          </button>
        ))}
        <select
          title="Font size"
          onChange={(e) => exec('fontSize', e.target.value)}
          defaultValue=""
          style={{
            border: `1px solid ${C.border}`, borderRadius: 3,
            padding: '1px 4px', fontSize: 11, color: C.textSec,
            fontFamily: FONT, cursor: 'pointer', background: 'none',
          }}
        >
          <option value="" disabled>Size</option>
          {[1,2,3,4,5,6,7].map(n => (
            <option key={n} value={n}>{[8,10,12,14,18,24,36][n-1]}px</option>
          ))}
        </select>
        <input
          type="color"
          title="Font color"
          onChange={(e) => exec('foreColor', e.target.value)}
          style={{ width: 28, height: 24, border: `1px solid ${C.border}`, borderRadius: 3, cursor: 'pointer', padding: 1 }}
        />
      </div>

      {/* Editor area */}
      <div style={{ position: 'relative', minHeight: 120 }}>
        {empty && (
          <span style={{
            position: 'absolute', top: 10, left: 12,
            color: '#bfbfbf', pointerEvents: 'none', fontSize: 13, fontFamily: FONT,
          }}>
            {placeholder}
          </span>
        )}
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onInput={handleInput}
          style={{
            minHeight: 120, padding: '10px 12px',
            outline: 'none', fontFamily: "'Calibri', 'Segoe UI', sans-serif",
            fontSize: 13, lineHeight: 1.6, color: C.text, wordBreak: 'break-word',
          }}
        />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SignatureForm — create or edit
// ─────────────────────────────────────────────────────────────────────────────
function SignatureForm({ initial, onSave, onCancel, saving }) {
  const [name,    setName]    = useState(initial?.name    || '');
  const [content, setContent] = useState(initial?.content || '');

  const isEdit   = !!initial?.id;
  const canSave  = name.trim() && content.trim();

  return (
    <div style={{ background: C.bg, border: `1px solid ${C.border}`, borderRadius: 6, padding: 20, marginBottom: 16 }}>
      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'block', fontSize: 12, color: C.textMuted, marginBottom: 4, fontFamily: FONT }}>
          Signature name
        </label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. Work, Personal"
          style={{
            width: '100%', boxSizing: 'border-box',
            border: `1px solid ${C.border}`, borderRadius: 4,
            padding: '7px 10px', fontSize: 13, fontFamily: FONT,
            outline: 'none', color: C.text,
          }}
          onFocus={e => e.currentTarget.style.borderColor = C.blue}
          onBlur={e  => e.currentTarget.style.borderColor = C.border}
        />
      </div>

      <div style={{ marginBottom: 14 }}>
        <label style={{ display: 'block', fontSize: 12, color: C.textMuted, marginBottom: 4, fontFamily: FONT }}>
          Signature content
        </label>
        <SigEditor value={content} onChange={setContent} />
      </div>

      <div style={{ display: 'flex', gap: 8 }}>
        <button
          onClick={() => canSave && onSave({ name: name.trim(), content })}
          disabled={!canSave || saving}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '7px 18px', background: canSave && !saving ? C.blue : '#c8c6c4',
            color: '#fff', border: 'none', borderRadius: 4,
            fontSize: 13, fontWeight: 600, fontFamily: FONT,
            cursor: canSave && !saving ? 'pointer' : 'not-allowed',
          }}
        >
          {saving
            ? <Spin indicator={<LoadingOutlined style={{ fontSize: 12, color: '#fff' }} spin />} />
            : <SaveOutlined />}
          {isEdit ? 'Save changes' : 'Create signature'}
        </button>
        <button
          onClick={onCancel}
          style={{
            padding: '7px 14px', background: 'none',
            border: `1px solid ${C.border}`, borderRadius: 4,
            fontSize: 13, fontFamily: FONT, cursor: 'pointer', color: C.textSec,
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// SignatureCard — single signature row
// ─────────────────────────────────────────────────────────────────────────────
function SignatureCard({ sig, isDefault, onSetDefault, onEdit, onDelete }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12,
      padding: '14px 16px',
      border: `1px solid ${isDefault ? C.blue : C.border}`,
      borderRadius: 6, marginBottom: 10,
      background: isDefault ? C.blueLight : C.bg,
      transition: 'border-color .15s, background .15s',
    }}>
      {/* Default toggle */}
      <Tooltip title={isDefault ? 'Default signature (click to unset)' : 'Set as default'}>
        <button
          onClick={() => onSetDefault(isDefault ? '' : sig.id)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, marginTop: 1, flexShrink: 0 }}
        >
          {isDefault
            ? <CheckCircleFilled style={{ fontSize: 20, color: C.blue }} />
            : <CheckCircleOutlined style={{ fontSize: 20, color: C.textMuted }} />}
        </button>
      </Tooltip>

      {/* Name + preview */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: C.text, fontFamily: FONT }}>{sig.name}</span>
          {isDefault && (
            <span style={{
              fontSize: 11, padding: '1px 8px', borderRadius: 10,
              background: C.blue, color: '#fff', fontFamily: FONT, fontWeight: 500,
            }}>Default</span>
          )}
        </div>
        <div
          style={{
            fontSize: 12, color: C.textSec, lineHeight: 1.5,
            maxHeight: 60, overflow: 'hidden',
            fontFamily: "'Calibri','Segoe UI',sans-serif",
          }}
          dangerouslySetInnerHTML={{ __html: sig.content }}
        />
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
        <Tooltip title="Edit">
          <button onClick={() => onEdit(sig)} style={iconBtnStyle}>
            <EditOutlined style={{ fontSize: 14 }} />
          </button>
        </Tooltip>
        <Tooltip title="Delete">
          <button onClick={() => onDelete(sig)} style={{ ...iconBtnStyle, color: C.red }}>
            <DeleteOutlined style={{ fontSize: 14 }} />
          </button>
        </Tooltip>
      </div>
    </div>
  );
}

const iconBtnStyle = {
  width: 30, height: 30,
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'none', border: `1px solid ${C.border}`,
  borderRadius: 4, cursor: 'pointer', color: C.textSec,
};

// ─────────────────────────────────────────────────────────────────────────────
// OutlookSignatureManager
// ─────────────────────────────────────────────────────────────────────────────
export default function OutlookSignatureManager({ onClose }) {
  const dispatch    = useDispatch();
  const loginUser   = useSelector(s => s?.loginUserInfo?.loginUserDetails);
  const signatures  = useSelector(s => s?.sales?.signatures || []);

  const employeeId  = loginUser?.id || '';

  const [loading,    setLoading]    = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [showForm,   setShowForm]   = useState(false);   // create form visible
  const [editTarget, setEditTarget] = useState(null);    // sig being edited
  const [defaultId,  setDefaultId]  = useState(() => getDefaultSignatureId(employeeId));
  const [confirmSig, setConfirmSig] = useState(null);    // sig pending delete confirm

  // Load signatures on mount
  useEffect(() => {
    if (!employeeId) return;
    setLoading(true);
    dispatch(fetchAllSignatures(employeeId)).finally(() => setLoading(false));
  }, [employeeId, dispatch]);

  // ── Create ────────────────────────────────────────────────────────────────
  const handleCreate = useCallback(async ({ name, content }) => {
    setSaving(true);
    await dispatch(saveSignature({ name, content, employeeId }));
    setSaving(false);
    setShowForm(false);
  }, [dispatch, employeeId]);

  // ── Edit ──────────────────────────────────────────────────────────────────
  const handleUpdate = useCallback(async ({ name, content }) => {
    setSaving(true);
    await dispatch(updateSignatureThunk({ id: editTarget.id, name, content }));
    setSaving(false);
    setEditTarget(null);
  }, [dispatch, editTarget]);

  // ── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = useCallback(async (sig) => {
    setConfirmSig(null);
    await dispatch(deleteSignatureThunk(sig.id));
    // Clear default if the deleted sig was default
    if (defaultId === sig.id) {
      setDefaultSignatureId(employeeId, '');
      setDefaultId('');
    }
  }, [dispatch, defaultId, employeeId]);

  // ── Set default ───────────────────────────────────────────────────────────
  const handleSetDefault = useCallback((sigId) => {
    setDefaultSignatureId(employeeId, sigId);
    setDefaultId(sigId);
  }, [employeeId]);

  return (
    <>
      <style>{CSS}</style>
      <div style={s.panel}>

        {/* Header */}
        <div style={s.header}>
          <span style={s.title}>Email Signatures</span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {!showForm && !editTarget && (
              <button
                onClick={() => setShowForm(true)}
                style={s.newBtn}
              >
                <PlusOutlined style={{ fontSize: 12 }} />
                New signature
              </button>
            )}
            {onClose && (
              <button onClick={onClose} style={{ ...iconBtnStyle, border: 'none' }}>
                <CloseOutlined style={{ fontSize: 14 }} />
              </button>
            )}
          </div>
        </div>

        {/* Body */}
        <div style={s.body}>

          {/* Create form */}
          {showForm && (
            <SignatureForm
              onSave={handleCreate}
              onCancel={() => setShowForm(false)}
              saving={saving}
            />
          )}

          {/* Edit form */}
          {editTarget && (
            <SignatureForm
              initial={editTarget}
              onSave={handleUpdate}
              onCancel={() => setEditTarget(null)}
              saving={saving}
            />
          )}

          {/* Signature list */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <Spin indicator={<LoadingOutlined style={{ fontSize: 24, color: C.blue }} spin />} />
            </div>
          ) : signatures.length === 0 ? (
            <div style={s.empty}>
              <div style={{ fontSize: 32, marginBottom: 8 }}>✍️</div>
              <div style={{ fontSize: 14, color: C.textSec, fontFamily: FONT }}>No signatures yet</div>
              <div style={{ fontSize: 12, color: C.textMuted, fontFamily: FONT, marginTop: 4 }}>
                Click "New signature" to create your first one
              </div>
            </div>
          ) : (
            signatures.map(sig => (
              <SignatureCard
                key={sig.id}
                sig={sig}
                isDefault={defaultId === sig.id}
                onSetDefault={handleSetDefault}
                onEdit={s => { setEditTarget(s); setShowForm(false); }}
                onDelete={setConfirmSig}
              />
            ))
          )}
        </div>

        {/* Default-signature info footer */}
        {signatures.length > 0 && (
          <div style={s.footer}>
            <CheckCircleFilled style={{ color: C.blue, fontSize: 13, marginRight: 6 }} />
            <span style={{ fontSize: 12, color: C.textSec, fontFamily: FONT }}>
              {defaultId
                ? `Default: "${signatures.find(s => s.id === defaultId)?.name || '—'}" — auto-inserted when composing new mail`
                : 'No default set — click ○ on a signature to make it default'}
            </span>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <Modal
        open={!!confirmSig}
        title="Delete signature"
        okText="Delete"
        okButtonProps={{ danger: true }}
        cancelText="Cancel"
        onOk={() => handleDelete(confirmSig)}
        onCancel={() => setConfirmSig(null)}
      >
        <p style={{ fontFamily: FONT, fontSize: 13 }}>
          Delete <strong>{confirmSig?.name}</strong>? This cannot be undone.
        </p>
      </Modal>
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Styles
// ─────────────────────────────────────────────────────────────────────────────
const s = {
  panel: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: C.bg,
    fontFamily: FONT,
    overflow: 'hidden',
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '14px 20px',
    borderBottom: `1px solid ${C.border}`,
    flexShrink: 0,
  },
  title: {
    fontSize: 16, fontWeight: 600, color: C.text, fontFamily: FONT,
  },
  newBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '6px 14px', background: C.blue, color: '#fff',
    border: 'none', borderRadius: 4, fontSize: 13, fontWeight: 600,
    fontFamily: FONT, cursor: 'pointer',
  },
  body: {
    flex: 1, minHeight: 0, overflowY: 'auto', padding: '16px 20px',
  },
  empty: {
    display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '60px 20px',
    color: C.textMuted,
  },
  footer: {
    borderTop: `1px solid ${C.border}`,
    padding: '10px 20px',
    background: C.bgAlt,
    display: 'flex', alignItems: 'center',
    flexShrink: 0,
  },
};

const CSS = `
  .osm-editor:focus { outline: none; }
`;
